import { useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { placeOrderAndPay } from "@/api/Orders/OrdersAPI";
import { OrderPlacedPaymentFailedError } from "@/api/Orders/OrdersAPI.model";
import { buildCreateOrderPayload } from "@/api/Orders/buildOrderPayload";
import { cartSubtotalGrosze, tipFeeGrosze } from "@/helpers/cartPricing";
import { orderPaths } from "@/routes/paths";
import { useCartStore } from "@/store/useCartStore";
import { useLoyaltyStore } from "@/store/useLoyaltyStore";
import { useVenuesDataStore } from "@/store/useVenuesDataStore";
import { paymentInstrumentHash } from "@/helpers/paymentHash";

/**
 * Places the order and takes payment for it, once.
 *
 * Double submission is guarded three ways, because a duplicate here means a
 * duplicate order and a second charge: the status check below, the disabled
 * button on the payment step, and clearing the cart on the confirmation screen
 * so a back-navigation has nothing to resubmit. (The bill-splitting flow's
 * equivalent has none of these — its Pay button stays enabled while in flight.)
 */
export const useSubmitOrder = () => {
  const { restaurantId = "", tableId = "" } = useParams<{
    restaurantId: string;
    tableId: string;
  }>();
  const navigate = useNavigate();

  const submit = useCallback(async () => {
    // Read imperatively so this callback is not re-created on every keystroke.
    const state = useCartStore.getState();
    if (state.status === "submitting" || state.lines.length === 0) return;

    const paths = orderPaths(restaurantId, tableId);
    const subtotal = cartSubtotalGrosze(state.lines);

    // What the card is actually charged: the dishes, the tip, and the venue's
    // fee on that tip, which the backend adds itself. Only the first two are
    // sent; sending the fee too would charge it twice.
    const tipFee = tipFeeGrosze(
      state.tipGrosze,
      useVenuesDataStore.getState().tipFeeBps,
    );
    const chargedGrosze = subtotal + state.tipGrosze + tipFee;

    state.setStatus("submitting");

    try {
      await placeOrderAndPay(
        restaurantId,
        buildCreateOrderPayload(tableId, state.lines),
        {
          method: "custom",
          amountGrosze: subtotal,
          tipGrosze: state.tipGrosze,
          paymentMethodName: state.paymentMethod,
        },
      );
      // The visit counts once the payment is through.
      useLoyaltyStore
        .getState()
        .registerPayment(
          chargedGrosze,
          paymentInstrumentHash(state.paymentMethod, state.blikCode),
        );
      useCartStore.getState().setStatus("idle");
      navigate(paths.confirmed);
    } catch (error) {
      useCartStore.getState().setStatus("error");

      // The dishes are already on the bill: keeping them in the cart would
      // invite a second identical order.
      const orderPlaced = error instanceof OrderPlacedPaymentFailedError;
      if (orderPlaced) useCartStore.getState().clear();

      navigate(paths.failed, { state: { orderPlaced } });
    }
  }, [restaurantId, tableId, navigate]);

  return { submit };
};
